import { useLiveQuery } from 'dexie-react-hooks';
import { Lock, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { birrInputValue, EXPENSE_UNITS, newId, PAYMENT_METHODS, tryParseBirr, type PaymentMethod } from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { IntInput, MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Badge, Button, Card, Field, Input, PageHeader, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, remove, saveMany, savePhotoWith, type Change } from '@/db/repo';
import { rememberLastUsed, useAnimals, useLastUsed, useToday } from '@/db/hooks';
import { PhotoPicker, type PickedPhoto } from '@/features/animals/common';
import { CategorySelect } from './CategorySelect';

/** Screen 18 — Expense form (EX-1, EX-3, PL-13). An event's expense: amount, date, animal and category are read-only. */
export function ExpenseFormPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const navigate = useNavigate();
  const last = useLastUsed();
  const animals = useAnimals() ?? [];
  const existing = useLiveQuery(() => (id ? db.expenses.get(id) : undefined), [id]);
  const [date, setDate] = useState(today);
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [method, setMethod] = useState<PaymentMethod>((last.paymentMethod as PaymentMethod) ?? 'CASH');
  const [covers, setCovers] = useState('1');
  const [animalId, setAnimalId] = useState('');
  const [photo, setPhoto] = useState<PickedPhoto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setDate(existing.date);
    setCategoryId(existing.categoryId);
    setAmount(birrInputValue(existing.amountSantim));
    setDescription(existing.description ?? '');
    setQuantity(existing.quantity ? String(existing.quantity) : '');
    setUnit(existing.unit ?? '');
    setPaidTo(existing.paidTo ?? '');
    setMethod(existing.method);
    setCovers(String(existing.coversMonths));
    setAnimalId(existing.animalId ?? '');
  }, [existing]);

  const fromEvent = !!existing?.sourceEventId;
  const locked = !!existing && !isOwner && (existing.createdBy !== profile.userId || isLocked(existing, profile, s));

  const submit = async () => {
    const a = tryParseBirr(amount);
    if (!categoryId) return setError(t('expenses.errors.category'));
    if (!a.ok || !a.santim || a.santim > 1_000_000_000) return setError(t('expenses.errors.amount'));
    const n = Number(covers);
    if (!Number.isInteger(n) || n < 1 || n > 12) return setError(t('expenses.errors.covers'));
    const q = quantity.trim() ? Number(quantity.replace(',', '.')) : null;
    if (q !== null && (!Number.isFinite(q) || q <= 0)) return setError(t('expenses.errors.quantity'));
    const record = {
      ...(existing ?? {}),
      id: existing?.id ?? newId(),
      date,
      categoryId,
      amountSantim: a.santim,
      description: description.trim() || null,
      quantity: q,
      unit: q !== null && unit ? unit : null,
      paidTo: paidTo.trim() || null,
      method,
      coversMonths: n,
      animalId: animalId || null,
      receiptFileId: photo ? photo.id : (existing?.receiptFileId ?? null),
    };
    try {
      const changes: Change[] = [{ entity: 'expense', op: 'upsert', record }];
      if (photo) await savePhotoWith({ id: photo.id, kind: 'RECEIPT', blob: photo.blob }, changes);
      else await saveMany(changes);
      await rememberLastUsed('paymentMethod', method);
      toast(navigator.onLine ? t('common.saved') : t('common.savedOffline'));
      navigate('/expenses', { replace: true });
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-8">
      <PageHeader title={existing ? t('expenses.edit') : t('expenses.add')} />
      {locked ? (
        <p className="flex items-center gap-2 rounded-xl bg-stone-200 p-3">
          <Lock className="h-4 w-4" /> {t('common.lockedAskOwner')}
        </p>
      ) : null}
      {fromEvent ? (
        <p className="rounded-xl bg-sky-50 p-3 text-sky-950">
          <Badge className="mr-2">{t('expenses.fromEvent')}</Badge>
          {t('expenses.fromEventHint')}{' '}
          <Link className="font-semibold underline" to={`/events/${existing!.sourceEventId}/edit`}>
            {t('expenses.openEvent')}
          </Link>
        </p>
      ) : null}
      <fieldset disabled={locked}>
        <Card className="space-y-4">
          <Field label={t('common.date')}>
            <DateField value={date} max={today} onChange={setDate} className={fromEvent ? 'pointer-events-none opacity-60' : ''} />
          </Field>
          <Field label={t('expenses.category')}>
            <CategorySelect value={categoryId} onChange={setCategoryId} disabled={fromEvent} includeInactiveId={existing?.categoryId} />
          </Field>
          <Field label={t('money.amount')}>
            <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} disabled={fromEvent} />
          </Field>
          <Field label={t('expenses.coversMonths')} hint={t('expenses.coversHint')}>
            <IntInput value={covers} onChange={(e) => setCovers(e.target.value.replace(/\D/g, '').slice(0, 2))} disabled={fromEvent} />
          </Field>
          <Field label={t('money.description')}>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('expenses.quantity')}>
              <Input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </Field>
            <Field label={t('expenses.unit')}>
              <Select value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="" />
                {EXPENSE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {t(`expenses.unitType.${u}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={t('expenses.paidTo')}>
            <Input value={paidTo} onChange={(e) => setPaidTo(e.target.value)} maxLength={80} />
          </Field>
          <Field label={t('money.method')}>
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`money.methodType.${m}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('expenses.animalOptional')}>
            <Select value={animalId} onChange={(e) => setAnimalId(e.target.value)} disabled={fromEvent}>
              <option value="">{t('common.none')}</option>
              {animals.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.tag} {a.name ?? ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('photo.receipt')}>
            <PhotoPicker value={photo} onChange={setPhoto} existingFileId={existing?.receiptFileId} />
          </Field>
          {error ? <p className="text-red-700">{error}</p> : null}
          <Button size="lg" className="w-full" onClick={() => void submit()}>
            {t('common.save')}
          </Button>
          {existing && !fromEvent ? (
            <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-5 w-5" /> {t('common.delete')}
            </Button>
          ) : null}
        </Card>
      </fieldset>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('expenses.deleteTitle')}
        danger
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          if (!existing) return;
          await remove('expense', existing.id);
          navigate('/expenses', { replace: true });
        }}
      />
    </div>
  );
}
